// Phosphor palette: one accent, and status colors that always mean the same
// thing. Green is the accent and "OK", gold means "needs attention", purple
// marks honors (lifetime, VIP, Golden Wookiee), red is errors and destructive
// actions only. Everything else is neutral. The one exception is member type
// (captain, member, red shirt, pending): those keep identity colors, drawn as
// filled pills (.kb-type in assets/css/phosphor.css) so they never read as a
// status tag.
//
// Shared by the PrimeVue preset (themes/krewebacchus.ts) and Tailwind
// (tailwind.config.js), which remaps Tailwind's stock hues onto these scales so
// existing `text-amber-500`-style classes follow the same rules.

export type Scale = Record<'50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | '950', string>

// Brand neon sits at 400 (dark-mode accent); 700 is the light-mode accent,
// dark enough for white text.
export const accent: Scale = {
  50: '#f0ffeb',
  100: '#dcffd2',
  200: '#bbffa8',
  300: '#8cff70',
  400: '#39ff14',
  500: '#2ee010',
  600: '#22b00b',
  700: '#17800b',
  800: '#13650c',
  900: '#11530e',
  950: '#052e04',
}

export const gold: Scale = {
  50: '#fff9e8',
  100: '#fff0c2',
  200: '#ffe08a',
  300: '#ffd05c',
  400: '#ffc53d',
  500: '#f2a90f',
  600: '#c98400',
  700: '#9a6400',
  800: '#7a4f05',
  900: '#5e3d08',
  950: '#352103',
}

export const honor: Scale = {
  50: '#f6f1ff',
  100: '#ede3ff',
  200: '#dccbff',
  300: '#c3a5ff',
  400: '#a97cff',
  500: '#8e55f7',
  600: '#7a3ae6',
  700: '#6a2ec9',
  800: '#5927a4',
  900: '#4a2484',
  950: '#2e125a',
}

export const alert: Scale = {
  50: '#fff1f1',
  100: '#ffe0e0',
  200: '#ffc6c7',
  300: '#ff9ea1',
  400: '#ff7a7e',
  500: '#f5454b',
  600: '#d92830',
  700: '#b51f27',
  800: '#941d23',
  900: '#7a1d22',
  950: '#43090c',
}

// Cool neutral for what used to be blue/sky/cyan/teal "info" styling.
export const steel: Scale = {
  50: '#f4f6f7',
  100: '#e6eaed',
  200: '#ccd4d9',
  300: '#a9b5bd',
  400: '#83929c',
  500: '#66757f',
  600: '#505d66',
  700: '#414c54',
  800: '#343d44',
  900: '#2a3238',
  950: '#1a2025',
}

// Surfaces, with a slight green bias. 950/900/800 are the dark page, pane and
// raised levels; 100/50/0 are their light counterparts.
export const graphite: Scale & { 0: string } = {
  0: '#ffffff',
  50: '#f9faf7',
  100: '#eef0eb',
  200: '#d6dbd2',
  300: '#b9c1ba',
  400: '#929d95',
  500: '#6f7a72',
  600: '#4a534c',
  700: '#2c332e',
  800: '#1b201c',
  900: '#101311',
  950: '#0b0d0c',
}
